/**
 * LE GRAPHIQUE D'UNE SOIRÉE SUR TOUTE LA NUIT — ce que la tuile Analytics
 * décide, sans React (19/10/2026), pour être testé.
 *
 * Le 15/10, la tuile de L'ÉTUDIANTE disait 710 entrées quand la porte en
 * avait vu 934 : MEDECINE (Pretix), rattachée à la soirée, en apportait 224.
 * Le serveur choisit la portée, la ventilation et le pas selon les Réglages
 * Soirées › Billetteries, et les renvoie ; la tuile les montre et en déduit
 * la forme du graphique.
 */

export type Portee = 'soiree' | 'nuit'

/** Les accents Live Control qu'une billetterie peut porter (réglage). */
const COULEURS = new Set(['violet', 'cyan', 'emerald', 'amber', 'rose', 'blue'])

/** `violet` → `var(--violet)` ; une valeur inconnue n'est jamais injectée telle quelle dans un style. */
export function couleurCss(token: string | undefined | null): string | undefined {
  return token && COULEURS.has(token) ? `var(--${token})` : undefined
}

/**
 * La FORME par défaut quand personne ne l'a choisie : sur toute la nuit
 * ventilée par billetterie, le CUMUL EMPILÉ — son bord haut est le total de
 * la nuit, chaque couche la part d'une billetterie. Sinon le cumul, comme
 * avant.
 */
export function formeParDefaut(portee: Portee, ventilation: string): 'stacked_cumulative' | 'cumulative' {
  return portee === 'nuit' && ventilation === 'billetterie' ? 'stacked_cumulative' : 'cumulative'
}

/**
 * La forme RÉELLEMENT tracée. Un empilement sans rien à empiler (une seule
 * série) retombe sur sa forme simple : des barres, ou le cumul.
 */
export function formeEffective(forme: string, ventilation: string, nbSeries: number): string {
  if (forme === 'stacked' && (ventilation === 'none' || nbSeries < 2)) return 'bars'
  if (forme === 'stacked_cumulative' && (ventilation === 'none' || nbSeries < 2)) return 'cumulative'
  return forme
}

/** « L'ÉTUDIANTE + 1 événement rattaché » — le choix « Toute la nuit » du sélecteur. */
export function libelleToutLaNuit(rattaches: number): string {
  return rattaches <= 0 ? 'Toute la nuit' : `Toute la nuit (+ ${rattaches} rattaché${rattaches > 1 ? 's' : ''})`
}

/**
 * Ce que l'écart du panneau compare.
 *   · NUIT    — le total des séries tracées (toute la nuit) ;
 *   · SHOTGUN — la série Shotgun quand on ventile par billetterie, sinon la
 *               série cachée envoyée par le serveur (`__cible`) ;
 *   · SOIREE  — le total (une seule série : elle-même).
 */
export function ecartContre(cible: string | undefined, ventilation: string, avecCibleServeur: boolean): string | undefined {
  if (cible !== 'SHOTGUN') return undefined
  if (ventilation === 'billetterie') return 'shotgun'
  return avecCibleServeur ? '__cible' : undefined
}

/**
 * Le nom de la référence dans le panneau : « N-1 », « N-2 »… pour les
 * comparaisons datées, « la référence » sinon. « Même famille » renvoie la
 * soirée précédente de la famille : l'appeler « N-1 » était faux (revue du
 * 19/10/2026).
 */
export function nomDeLaReference(compare: string | undefined): string {
  if (compare === 'SAME_WEEKDAY_N1' || compare === 'SAME_DATE_N1') return 'N-1'
  if (compare === 'SAME_WEEKDAY_N2') return 'N-2'
  if (compare === 'SAME_WEEKDAY_N3') return 'N-3'
  return 'la référence'
}

/**
 * Ce que dit l'écart. `nuitDeReference` : la référence compte aussi ses
 * événements rattachés (nuit contre nuit). `partiel` : une série est masquée
 * dans la légende — l'écart ne compare plus la nuit, il n'est pas affiché
 * (voir `ecartAffichable`).
 */
export function libelleEcart(cible: string | undefined, compare?: string, nuitDeReference = false): string | undefined {
  const ref = nomDeLaReference(compare)
  if (cible === 'NUIT') return nuitDeReference ? `toute la nuit vs nuit ${ref === 'la référence' ? 'de référence' : ref}` : `toute la nuit vs ${ref}`
  if (cible === 'SHOTGUN') return `Shotgun vs ${ref}`
  return undefined
}

/**
 * L'écart du panneau a-t-il un sens ? Pas quand on a masqué une des séries
 * qu'il additionne : « Entrées BDE 224 · −47 % toute la nuit vs N-1 »
 * comparait Pretix seul au N-1 (revue du 19/10/2026). Le Shotgun contre
 * Shotgun lit sa propre série : il ne dépend que d'elle.
 */
export function ecartAffichable(contre: string | undefined, masquees: ReadonlySet<string>): boolean {
  if (contre === '__cible') return true
  if (contre) return !masquees.has(contre)
  return [...masquees].every((k) => k === '__ref' || k === '__cible')
}

/**
 * La bande de prévision (Shotgun) est-elle lisible ? Elle se lit contre la
 * couche SHOTGUN. Sur toute la nuit sans ventilation par billetterie, il n'y
 * a qu'une courbe Shotgun + Pretix : 934 au-dessus d'une bande de ~700 se
 * lisait « la nuit bat la prévision de 30 % » (graphique.md §3.4). Masquée,
 * et l'écran dit pourquoi.
 */
export function bandeLisible(portee: Portee, plusieursBilletteries: boolean, ventilation: string, forme: string): { ok: boolean; raison?: string } {
  if (['bars', 'grouped', 'stacked'].includes(forme)) return { ok: false }
  if (portee === 'nuit' && plusieursBilletteries && ventilation !== 'billetterie') {
    return { ok: false, raison: 'La prévision ne concerne que Shotgun : découpez la nuit « Par billetterie », ou passez sur « Cette soirée seule », pour la voir.' }
  }
  return { ok: true }
}
